import { toast } from '@crowdex/ui/components/toaster';
import { type ReactNode, useRef, useState } from 'react';
import type { ArtifactTypeEntry, PluginPanel } from '@/features/plugins/plugin-workbench';
import { startAppConversation } from './app-conversation';
import { FloatingComposerForm } from './floating-composer-form';

/** A type's guided creation, as an app's home offers it (`types[].create` in `crowdex.json`). */
export type AppCreation = {
  /** "New automation", or the type's own `label`. */
  label: string;
  /** The type's noun ("automation"). */
  noun: string;
  skill: NonNullable<ArtifactTypeEntry['create']>;
};

/** The guided creation of an app's home: its first type that declares one, else none. */
export function appCreation(panel: Pick<PluginPanel, 'artifactTypes'>): AppCreation | null {
  const type = panel.artifactTypes?.find((entry) => entry.create !== undefined);
  if (type?.create === undefined) return null;
  const noun = type.title.toLowerCase();
  return { label: type.create.label ?? `New ${noun}`, noun, skill: type.create };
}

/**
 * The first message of a guided creation: its skill, as the composer references one
 * (`[$name](skill://…/SKILL.md)`, which gives the agent the whole `SKILL.md`), then what the
 * user wrote, if anything.
 */
export function creationMessage(creation: AppCreation, text = ''): string {
  const skill = `[$${creation.skill.skill}](${creation.skill.uri})`;
  return text.trim() === '' ? skill : `${skill} ${text.trim()}`;
}

/**
 * A guided creation: a new, plain conversation (filed in no assistant: an assistant is its own
 * conversation, docs/adr-assistants.md), the app docked, whose first message runs the type's
 * creation skill with what the user wrote.
 */
export function startCreation(
  panel: Pick<PluginPanel, 'view'>,
  creation: AppCreation,
  text?: string,
): Promise<void> {
  return startAppConversation(panel.view, { send: creationMessage(creation, text) });
}

/**
 * An app's home with a guided creation: "New …" starts it, and its floating composer starts it
 * with the user's message; an example prompt fills the composer. Null when no type declares one
 * (the home keeps its plain "New …").
 */
export function useAppCreation(panel: PluginPanel): {
  label: string;
  starting: boolean;
  start(text?: string): void;
  prefill(text: string): void;
  composer: ReactNode;
} | null {
  const creation = appCreation(panel);
  const [text, setText] = useState('');
  const [starting, setStarting] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  if (creation === null) return null;
  const start = (message?: string) => {
    if (starting) return;
    setStarting(true);
    void startCreation(panel, creation, message).then(
      () => {
        setStarting(false);
        if (message !== undefined) setText('');
      },
      (error: unknown) => {
        setStarting(false);
        toast.error(creation.label, error instanceof Error ? error.message : String(error));
      },
    );
  };
  return {
    label: creation.label,
    starting,
    start,
    prefill: (prompt) => {
      setText(prompt);
      input.current?.focus();
    },
    composer: (
      <div className="flex shrink-0 justify-center px-10 pt-2 pb-4 max-md:px-4" data-app-composer>
        <FloatingComposerForm
          className="w-[min(720px,100%)]"
          input={input}
          inputLabel={`Ask for a new ${creation.noun}`}
          label={creation.label}
          onChange={setText}
          onSend={start}
          placeholder={`Ask for a new ${creation.noun}…`}
          sending={starting}
          value={text}
        />
      </div>
    ),
  };
}
